import React, { useState } from "react";
import {
  Briefcase, Users, Target, Layers, Wrench, Building2, MapPin,
  Clock4, BadgeCheck, X, Plus, ChevronDown, ChevronUp,
} from "lucide-react";
import {
  TalentFilters,
  ROLE_OPTIONS,
  MANAGEMENT_LEVEL_OPTIONS,
  LEADERSHIP_RESPONSIBILITY_OPTIONS,
  IMPACT_AREA_OPTIONS,
  ACHIEVEMENT_SIGNAL_OPTIONS,
  SKILL_OPTIONS,
  INDUSTRY_OPTIONS,
  COMPANY_TYPE_OPTIONS,
  AVAILABILITY_SHIFT_OPTIONS,
  AVAILABILITY_NOTICE_OPTIONS,
  WORK_MODE_OPTIONS,
  RELOCATION_OPTIONS,
  WORK_PREFERENCE_OPTIONS,
  NEXGENT_SIGNAL_OPTIONS,
  TEAM_SIZE_BUCKETS,
  EXPERIENCE_BUCKETS,
  LEADERSHIP_EXPERIENCE_BUCKETS,
} from "../Types/TalentIntelligenceTypes";

interface AdvancedFilterPanelProps {
  open: boolean;
  filters: TalentFilters;
  onChange: (patch: Partial<TalentFilters>) => void;
  onClose: () => void;
  onApply: () => void;
}

/* ─── small building blocks ─────────────────────────────────────── */

const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            {icon}
          </span>
          <span className="text-sm font-bold text-slate-800">{title}</span>
        </span>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-slate-100">{children}</div>}
    </div>
  );
};

const OptionPills: React.FC<{
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  columns?: number;
}> = ({ options, selected, onToggle, columns = 2 }) => (
  <div className={`grid gap-1.5 ${columns === 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
    {options.map((opt) => {
      const active = selected.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onToggle(opt)}
          className={`px-2.5 py-1.5 rounded-lg border text-xs font-semibold text-left transition-all ${
            active
              ? "border-indigo-400 bg-indigo-50 text-indigo-700 shadow-sm"
              : "border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:bg-indigo-50/40"
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const BucketButtons: React.FC<{
  buckets: { label: string; min: number }[];
  value?: number;
  onSelect: (min: number | undefined) => void;
  allowCustom?: boolean;
}> = ({ buckets, value, onSelect, allowCustom = false }) => (
  <div className="flex flex-wrap gap-1.5">
    {buckets.map((b) => (
      <button
        key={b.label}
        type="button"
        onClick={() => onSelect(value === b.min ? undefined : b.min)}
        className={`px-2.5 py-1.5 rounded-lg border text-xs font-bold transition-all ${
          value === b.min
            ? "border-indigo-400 bg-indigo-600 text-white shadow-sm"
            : "border-slate-200 bg-white text-slate-600 hover:border-indigo-200"
        }`}
      >
        {b.label}
      </button>
    ))}
  </div>
);

/** Mirrors the dashboard's experience segments exactly (each band is its own
 *  inclusive range with BOTH bounds), so a dashboard click is fully visible
 *  here "as it is": <10, 10-12, 12-15, 15-20, 20+. */
const RANGE_EXPERIENCE_BUCKETS: { label: string; min?: number; max?: number }[] = [
  { label: "<10", max: 9 },
  { label: "10-12", min: 10, max: 12 },
  { label: "12-15", min: 12, max: 15 },
  { label: "15-20", min: 15, max: 20 },
  { label: "20+", min: 20 },
];

const RangeBucketButtons: React.FC<{
  current: { min?: number; max?: number };
  onSelect: (range: { min?: number; max?: number }) => void;
}> = ({ current, onSelect }) => (
  <div className="flex flex-wrap gap-1.5">
    {RANGE_EXPERIENCE_BUCKETS.map((b) => {
      const active =
        (b.min ?? undefined) === (current.min ?? undefined) &&
        (b.max ?? undefined) === (current.max ?? undefined);
      return (
        <button
          key={b.label}
          type="button"
          onClick={() => onSelect(active ? {} : { min: b.min, max: b.max })}
          className={`px-2.5 py-1.5 rounded-lg border text-xs font-bold transition-all ${
            active
              ? "border-fuchsia-400 bg-fuchsia-600 text-white shadow-sm"
              : "border-slate-200 bg-white text-slate-600 hover:border-fuchsia-200"
          }`}
        >
          {b.label}
        </button>
      );
    })}
  </div>
);

const toggleIn = (list: string[], value: string): string[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const ChipList: React.FC<{
  items: string[];
  label?: string;
  onRemove: (item: string) => void;
}> = ({ items, label, onRemove }) => {
  if (items.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span key={item} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold">
          {item}
          <button type="button" onClick={() => onRemove(item)} className="rounded-full p-0.5 hover:bg-black/10">
            <X size={11} />
          </button>
        </span>
      ))}
      {label && <span className="text-[11px] text-slate-400 pl-1">{label}</span>}
    </div>
  );
};

/* ─── main panel ────────────────────────────────────────────────── */

export const AdvancedFilterPanel: React.FC<AdvancedFilterPanelProps> = ({
  open,
  filters,
  onChange,
  onClose,
  onApply,
}) => {
  const [customExpMin, setCustomExpMin] = useState("");
  const [customExpMax, setCustomExpMax] = useState("");
  const [customLeadExp, setCustomLeadExp] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const [newCity, setNewCity] = useState("");
  const [pendingSkill, setPendingSkill] = useState("");
  const [pendingSkillYears, setPendingSkillYears] = useState("");

  if (!open) return null;

  const skillGroups = SKILL_OPTIONS.reduce<Record<string, string[]>>((acc, s) => {
    (acc[s.group] = acc[s.group] || []).push(s.label);
    return acc;
  }, {});

  const addCompany = () => {
    const name = newCompany.trim();
    if (!name) return;
    if (filters.companies.includes(name)) return;
    onChange({ companies: [...filters.companies, name] });
    setNewCompany("");
  };

  const addSkillWithYears = () => {
    if (!pendingSkill || !pendingSkillYears) return;
    const entry = { skill: pendingSkill, years: Number(pendingSkillYears) };
    if (filters.skillsWithYears.some((s) => s.skill === pendingSkill)) return;
    onChange({ skillsWithYears: [...filters.skillsWithYears, entry] });
    setPendingSkill("");
    setPendingSkillYears("");
  };

  const addCity = () => {
    const city = newCity.trim();
    if (!city) return;
    if (filters.cities.includes(city)) {
      setNewCity("");
      return;
    }
    onChange({ cities: [...filters.cities, city] });
    setNewCity("");
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-[#F8FAFC] shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-base font-black text-slate-800 flex items-center gap-2">
              <Layers size={18} className="text-indigo-500" />
              Advanced Filters
            </h3>
            <p className="text-[11px] text-slate-400 font-medium">
              Filters combine with AND logic. Select multiple values inside a filter with OR logic.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            aria-label="Close filters"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 flex flex-col gap-3">
          {/* A. Professional Experience */}
          <SectionCard icon={<Briefcase size={14} />} title="Professional Experience">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Experience bands (matches dashboard)</p>
                <RangeBucketButtons
                  current={{ min: filters.totalExperienceMin, max: filters.totalExperienceMax }}
                  onSelect={(range) =>
                    onChange({ totalExperienceMin: range.min ?? undefined, totalExperienceMax: range.max ?? undefined })
                  }
                />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Total experience (min)</p>
                <BucketButtons
                  buckets={EXPERIENCE_BUCKETS}
                  value={filters.totalExperienceMin}
                  onSelect={(min) => onChange({ totalExperienceMin: min })}
                  allowCustom
                />
                {filters.totalExperienceMin !== undefined && filters.totalExperienceMax === undefined && (
                  <p className="text-[11px] text-indigo-600 mt-1">Showing profiles with {filters.totalExperienceMin}+ years</p>
                )}
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Custom experience range</p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    placeholder="Min"
                    value={customExpMin}
                    onChange={(e) => setCustomExpMin(e.target.value)}
                    className="w-24 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                  <span className="text-xs text-slate-400">to</span>
                  <input
                    type="number"
                    min={0}
                    placeholder="Max"
                    value={customExpMax}
                    onChange={(e) => setCustomExpMax(e.target.value)}
                    className="w-24 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                  <button
                    type="button"
                    onClick={() => onChange({
                      totalExperienceMin: customExpMin ? Number(customExpMin) : undefined,
                      totalExperienceMax: customExpMax ? Number(customExpMax) : undefined,
                    })}
                    className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Leadership experience</p>
                <BucketButtons
                  buckets={LEADERSHIP_EXPERIENCE_BUCKETS}
                  value={filters.leadershipExperienceMin}
                  onSelect={(min) => onChange({ leadershipExperienceMin: min })}
                />
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="number"
                    min={0}
                    placeholder="Custom leadership yrs"
                    value={customLeadExp}
                    onChange={(e) => setCustomLeadExp(e.target.value)}
                    className="w-36 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                  <button
                    type="button"
                    onClick={() => onChange({ leadershipExperienceMin: customLeadExp ? Number(customLeadExp) : undefined })}
                    className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Current / Target role</p>
                <OptionPills
                  options={ROLE_OPTIONS}
                  selected={filters.roles}
                  onToggle={(v) => onChange({ roles: toggleIn(filters.roles, v) })}
                />
              </div>
            </div>
          </SectionCard>

          {/* 3. Leadership Signals */}
          <SectionCard icon={<Users size={14} />} title="Leadership Signals">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Team size managed</p>
                <BucketButtons
                  buckets={TEAM_SIZE_BUCKETS}
                  value={filters.teamSizeMin}
                  onSelect={(min) => onChange({ teamSizeMin: min })}
                />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Management level</p>
                <OptionPills
                  options={MANAGEMENT_LEVEL_OPTIONS}
                  selected={filters.managementLevels}
                  onToggle={(v) => onChange({ managementLevels: toggleIn(filters.managementLevels, v) })}
                />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Leadership responsibilities</p>
                <OptionPills
                  options={LEADERSHIP_RESPONSIBILITY_OPTIONS}
                  selected={filters.leadershipResponsibilities}
                  onToggle={(v) => onChange({ leadershipResponsibilities: toggleIn(filters.leadershipResponsibilities, v) })}
                  columns={3}
                />
              </div>
            </div>
          </SectionCard>

          {/* 4. Business Impact */}
          <SectionCard icon={<Target size={14} />} title="Business Impact">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Impact areas</p>
                <OptionPills
                  options={IMPACT_AREA_OPTIONS}
                  selected={filters.impactAreas}
                  onToggle={(v) => onChange({ impactAreas: toggleIn(filters.impactAreas, v) })}
                  columns={3}
                />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Achievement signals</p>
                <OptionPills
                  options={ACHIEVEMENT_SIGNAL_OPTIONS}
                  selected={filters.achievementSignals}
                  onToggle={(v) => onChange({ achievementSignals: toggleIn(filters.achievementSignals, v) })}
                  columns={3}
                />
              </div>
            </div>
          </SectionCard>

          {/* 5. Technical Skills */}
          <SectionCard icon={<Wrench size={14} />} title="Technical Skills">
            <div className="flex flex-col gap-3">
              {Object.entries(skillGroups).map(([group, skills]) => (
                <div key={group}>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">{group}</p>
                  <OptionPills
                    options={skills}
                    selected={filters.skills}
                    onToggle={(v) => onChange({ skills: toggleIn(filters.skills, v) })}
                    columns={3}
                  />
                </div>
              ))}
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Skill + years of experience</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={pendingSkill}
                    onChange={(e) => setPendingSkill(e.target.value)}
                    className="flex-1 min-w-[140px] px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    <option value="">Select skill…</option>
                    {SKILL_OPTIONS.map((s) => (
                      <option key={s.label} value={s.label}>{s.label}</option>
                    ))}
                  </select>
                  <select
                    value={pendingSkillYears}
                    onChange={(e) => setPendingSkillYears(e.target.value)}
                    className="w-24 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    <option value="">Years</option>
                    {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((y) => (
                      <option key={y} value={y}>{y}+</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={addSkillWithYears}
                    className="p-2 rounded-lg bg-cyan-600 text-white hover:bg-cyan-700"
                    aria-label="Add skill with years"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                <ChipList
                  items={filters.skillsWithYears.map((s) => `${s.skill} ≥ ${s.years}yrs`)}
                  onRemove={(label) => {
                    const skillName = label.split(" ≥ ")[0];
                    onChange({ skillsWithYears: filters.skillsWithYears.filter((s) => s.skill !== skillName) });
                  }}
                />
              </div>
            </div>
          </SectionCard>

          {/* 6. Industry / Domain */}
          <SectionCard icon={<Layers size={14} />} title="Industry / Domain">
            <OptionPills
              options={INDUSTRY_OPTIONS}
              selected={filters.industries}
              onToggle={(v) => onChange({ industries: toggleIn(filters.industries, v) })}
              columns={3}
            />
          </SectionCard>

          {/* 7. Company Experience */}
          <SectionCard icon={<Building2 size={14} />} title="Company Experience">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Company type</p>
                <OptionPills
                  options={COMPANY_TYPE_OPTIONS}
                  selected={filters.companyTypes}
                  onToggle={(v) => onChange({ companyTypes: toggleIn(filters.companyTypes, v) })}
                />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Worked at (specific companies)</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newCompany}
                    onChange={(e) => setNewCompany(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addCompany(); }}
                    placeholder="e.g. Infosys, Google, AWS…"
                    className="flex-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                  <button
                    type="button"
                    onClick={addCompany}
                    className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700"
                  >
                    Add
                  </button>
                </div>
                <ChipList
                  items={filters.companies}
                  onRemove={(name) => onChange({ companies: filters.companies.filter((c) => c !== name) })}
                  label="companies"
                />
              </div>
            </div>
          </SectionCard>

          {/* 9+10. Location, Availability & Work Model */}
          <SectionCard icon={<MapPin size={14} />} title="Location & Work Model">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Location (city)</p>
                <div className="flex flex-wrap gap-1.5">
                  {["Pune", "Mumbai", "Bangalore", "Hyderabad", "Delhi", "Chennai"].map((city) => (
                    <button
                      key={city}
                      type="button"
                      onClick={() => onChange({ cities: toggleIn(filters.cities, city) })}
                      className={`px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all ${
                        filters.cities.includes(city)
                          ? "border-amber-400 bg-amber-50 text-amber-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-amber-200"
                      }`}
                    >
                      {city}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="text"
                    value={newCity}
                    onChange={(e) => setNewCity(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addCity(); }}
                    placeholder="Add any city…"
                    className="flex-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                  <button
                    type="button"
                    onClick={addCity}
                    className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700"
                  >
                    Add
                  </button>
                </div>
                <ChipList
                  items={filters.cities}
                  onRemove={(city) => onChange({ cities: filters.cities.filter((c) => c !== city) })}
                  label="cities"
                />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Work mode</p>
                <OptionPills
                  options={WORK_MODE_OPTIONS}
                  selected={filters.workModes}
                  onToggle={(v) => onChange({ workModes: toggleIn(filters.workModes, v) })}
                  columns={3}
                />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Relocation</p>
                <OptionPills
                  options={RELOCATION_OPTIONS}
                  selected={filters.relocations}
                  onToggle={(v) => onChange({ relocations: toggleIn(filters.relocations, v) })}
                  columns={3}
                />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Work preference</p>
                <OptionPills
                  options={WORK_PREFERENCE_OPTIONS}
                  selected={filters.workPreferences}
                  onToggle={(v) => onChange({ workPreferences: toggleIn(filters.workPreferences, v) })}
                  columns={3}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={<Clock4 size={14} />} title="Availability & Opportunity">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Opportunity status</p>
                <OptionPills
                  options={AVAILABILITY_SHIFT_OPTIONS}
                  selected={filters.availabilityShifts}
                  onToggle={(v) => onChange({ availabilityShifts: toggleIn(filters.availabilityShifts, v) })}
                />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Availability (notice period)</p>
                <div className="flex flex-wrap gap-1.5">
                  {AVAILABILITY_NOTICE_OPTIONS.map((opt) => {
                    const isOther = opt === "Other";
                    const active = isOther
                      ? filters.minNoticeDays !== undefined
                      : filters.maxNoticeDays === NOTICE_BOUNDS[opt];
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          if (isOther) {
                            onChange({ minNoticeDays: active ? undefined : 91, maxNoticeDays: active ? undefined : filters.maxNoticeDays });
                          } else {
                            onChange({ maxNoticeDays: active ? undefined : NOTICE_BOUNDS[opt], minNoticeDays: active ? undefined : filters.minNoticeDays });
                          }
                        }}
                        className={`px-2.5 py-1.5 rounded-lg border text-xs font-bold transition-all ${
                          active
                            ? "border-orange-400 bg-orange-50 text-orange-700"
                            : "border-slate-200 bg-white text-slate-600 hover:border-orange-200"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </SectionCard>

          {/* 11. Nexgent Signals */}
          <SectionCard icon={<BadgeCheck size={14} />} title="Nexgent Professional Signals">
            <OptionPills
              options={NEXGENT_SIGNAL_OPTIONS}
              selected={filters.nexgentSignals}
              onToggle={(v) => onChange({ nexgentSignals: toggleIn(filters.nexgentSignals, v) })}
              columns={3}
            />
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                { key: "leadershipScoreMin", label: "Leadership score ≥" },
                { key: "overallScoreMin", label: "Overall score ≥" },
                { key: "profileCompleteScoreMin", label: "Profile ≥" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <p className="text-[11px] font-bold text-slate-500 mb-1">{label}</p>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={(filters as unknown as Record<string, number | undefined>)[key] ?? ""}
                    onChange={(e) =>
                      onChange({
                        [key]: e.target.value ? Number(e.target.value) : undefined,
                      } as Partial<TalentFilters>)
                    }
                    className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
              ))}
            </div>
          </SectionCard>

          <button
            type="button"
            onClick={onApply}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white text-sm font-bold hover:opacity-90 transition-all shadow-md"
          >
            Apply Filters & Search
          </button>
        </div>
      </div>
    </div>
  );
};

const NOTICE_BOUNDS: Record<string, number> = {
  "Immediately available": 7,
  "15 days": 15,
  "30 days": 30,
  "60 days": 60,
  "90 days": 90,
};

export default AdvancedFilterPanel;